import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';

import { TEMPLATE_EMAIL_NAME } from '@api/security/model';
import { withAutoUpdate } from '@flux';
import { mainLeadsActionsPack } from '@leads/actions';
import { openUpdateLeadFormDrawer } from '@leads/components/forms';
import { mainLeadsSelectorsPack } from '@leads/selectors';
import { IAppState } from '@store';
import { LeadsEntry as XLeadsEntry, LeadsEntryProps as XLeadsEntryProps } from './entry.view';

export type LeadsEntryProps = {
	dateRange?: DateRange;
	fetchLeads?: typeof mainLeadsActionsPack.actions.fetchLeads;
	fetchNewLeadSmsText?: typeof mainLeadsActionsPack.actions.fetchNewLeadSmsText;
	sendEmailNotification?: typeof mainLeadsActionsPack.actions.sendEmailNotification;
} & Partial<Pick<XLeadsEntryProps, 'leads' | 'isFetching'>>;

const LeadsEntry: React.FC<LeadsEntryProps> = props => {
	const { dateRange, fetchLeads, isFetching, leads, sendEmailNotification, ...rest } = props;

	const handleRequestUpdateLead = (lead: Lead) =>
		openUpdateLeadFormDrawer({
			appearance: 'edit',
			lead,
		});

	const handleSendEmailNotification = (lead: Lead) =>
		sendEmailNotification({
			to: lead.ContactEmail,
			templateFileName: TEMPLATE_EMAIL_NAME.LEAD_ADDED,
		});

	return (
		<XLeadsEntry
			{...rest}
			isFetching={isFetching}
			leads={leads}
			onRequestUpdateLead={handleRequestUpdateLead}
			onSendEmailNotification={handleSendEmailNotification}
		/>
	);
};

LeadsEntry.displayName = 'LeadsEntry';
LeadsEntry.defaultProps = {};

function mapStateToProps(state: IAppState): Partial<LeadsEntryProps> {
	return {
		dateRange: mainLeadsSelectorsPack.selectDateRange(state),
		isFetching: mainLeadsSelectorsPack.selectAsyncLeads.selectIsFetching(state),
		leads: mainLeadsSelectorsPack.selectAsyncLeads.selectItem(state),
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return bindActionCreators(
		{
			fetchLeads: mainLeadsActionsPack.actions.fetchLeads,
			fetchNewLeadSmsText: mainLeadsActionsPack.actions.fetchNewLeadSmsText,
			sendEmailNotification: mainLeadsActionsPack.actions.sendEmailNotification,
		},
		dispatch,
	);
}

export default withAutoUpdate(
	mapStateToProps,
	mapDispatchToProps,
)({
	leadsDidInvalidate: [
		mainLeadsSelectorsPack.selectAsyncLeads.selectDidInvalidate,
		({ dateRange, fetchLeads }) => fetchLeads(dateRange),
	],
	newLeadSmsTextDidInvalidate: [
		mainLeadsSelectorsPack.selectAsyncNewLeadSmsText.selectDidInvalidate,
		({ fetchNewLeadSmsText }) => fetchNewLeadSmsText(),
	],
})(LeadsEntry);
